/**
 * @fileoverview The pieces every missing-notes list shares to clear notices in
 * bulk (lib/notesClearance): a tick box per row, a toolbar above the list that
 * selects all and offers what fits the selection, and a confirm step that
 * takes an optional note.
 *
 * The toolbar shows whenever the list has a row that can be acted on, so a
 * selection can be started from it; the actions appear once something is
 * ticked, each with the count it will apply to.
 */

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/studio/primitives";

const box = "h-4 w-4 shrink-0 cursor-pointer accent-[var(--accent-yellow)]";

/** One row's tick box. Clicks stop here, so ticking never opens the row. */
export function SelectBox({ checked, onChange, label, testId }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      aria-label={label}
      data-testid={testId}
      className={box}
    />
  );
}

/**
 * @param {{
 *   selection: { size: number, offered: number, allSelected: boolean, toggleAll: () => void, clear: () => void },
 *   noun?: string,
 *   actions: Array<{ id: string, label: string, onPress: () => void, primary?: boolean, danger?: boolean,
 *     icon?: string, disabled?: boolean }>,
 *   hint?: string, summary?: any, testId?: string,
 * }} props `summary` stands at the row's end while nothing is ticked (a list's
 *   count), where `hint` would; the actions take its place once something is.
 */
export function NotesClearToolbar({ selection, noun = "session", actions, hint, summary, testId = "notes-clear" }) {
  if (!selection.offered) return null;
  const plural = (n) => `${n} ${noun}${n === 1 ? "" : "s"}`;
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--studio-divider)] px-[18px] py-3"
      data-testid={testId}
    >
      <label className="flex cursor-pointer items-center gap-[14px] font-sans text-[14px] text-[var(--text-secondary)]">
        <input
          type="checkbox"
          checked={selection.allSelected}
          onChange={selection.toggleAll}
          aria-label={selection.allSelected ? "Clear selection" : `Select all ${plural(selection.offered)}`}
          data-testid={`${testId}-all`}
          className={box}
        />
        {selection.size ? `${selection.size} selected` : `Select all ${plural(selection.offered)}`}
      </label>
      {selection.size > 0 ? (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {actions.map((a) => (
            <Button
              key={a.id}
              variant={a.primary ? "primary" : a.danger ? "danger" : "ghost"}
              icon={a.icon}
              onPress={a.onPress}
              disabled={a.disabled}
              data-testid={`${testId}-${a.id}`}
              className="px-4 py-2 text-[14px]"
            >
              {a.label}
            </Button>
          ))}
          <button
            type="button"
            onClick={selection.clear}
            data-testid={`${testId}-none`}
            className="font-sans text-[13px] text-[var(--text-tertiary)] hover:underline"
          >
            Clear selection
          </button>
        </div>
      ) : summary ? (
        <span className="ml-auto font-sans text-[13px] text-[var(--text-tertiary)]" data-testid={`${testId}-summary`}>
          {summary}
        </span>
      ) : (
        hint && <span className="font-sans text-[13px] text-[var(--text-tertiary)]">{hint}</span>
      )}
    </div>
  );
}

/**
 * Confirm a clear, a request, a decline or a restore, with an optional note —
 * and the org lists' archive and delete, which take none.
 *
 * @param {{
 *   open: boolean, onOpenChange: (open: boolean) => void, title: string,
 *   description?: string, children?: any, noteLabel?: string, notePlaceholder?: string,
 *   confirmLabel: string, onConfirm: (note: string) => void, busy?: boolean,
 *   danger?: boolean, testId?: string,
 * }} props `danger` draws the confirm button as the one that cannot be undone.
 */
export function NotesClearDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  noteLabel,
  notePlaceholder,
  confirmLabel,
  onConfirm,
  busy = false,
  danger = false,
  testId = "notes-clear-dialog",
}) {
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) setNote("");
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">{title}</DialogTitle>
          {description && (
            <DialogDescription className="font-sans text-[14px] text-[var(--text-secondary)]">{description}</DialogDescription>
          )}
        </DialogHeader>
        {children}
        {noteLabel && (
          <label className="block">
            <span className="mb-1.5 block font-sans text-[13px] font-medium text-[var(--text-tertiary)]">{noteLabel}</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder={notePlaceholder}
              data-testid={`${testId}-note`}
              className="w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]"
            />
          </label>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button variant="bare" onPress={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>
            Cancel
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            onPress={() => onConfirm(note.trim())}
            disabled={busy}
            data-testid={`${testId}-confirm`}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
